import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_DEMO } from '../constants/featureFlags';

/**
 * Demo-only role switcher. The demo uses ONE login that holds teacher+admin+tls
 * claims; this control constrains the *view* to a single role so you can present
 * each role's experience separately. Switching updates React state via onPick
 * (so the view changes instantly — no page reload) and persists to localStorage
 * so the choice survives a manual refresh. ProtectedLayout derives the effective
 * role from the demoRole state (see getDemoRole for the persisted fallback).
 *
 * NOTE: Admin is intentionally omitted from this VIEW AS toggle — the demo only
 * previews Teacher and TLS. The underlying Admin role/claims are untouched; this
 * array only controls which views the switcher offers. Because getDemoRole()
 * validates the persisted value against this array, a stale 'admin' selection
 * from a prior build cleanly falls back to 'teacher'. Do not add 'admin' back
 * here without also intending it to be a presentable demo view.
 */
const ROLES = [
  { key: 'teacher', label: 'Teacher' },
  { key: 'tls', label: 'TLS' },
];

export function getDemoRole() {
  if (!IS_DEMO || typeof window === 'undefined') return null;
  const r = window.localStorage.getItem('demoRole');
  return ROLES.some((x) => x.key === r) ? r : 'teacher';
}

function DemoRoleSwitcher({ active, onPick }) {
  const navigate = useNavigate();
  if (!IS_DEMO) return null;
  const current = active || getDemoRole();

  const pick = (key) => {
    if (key === current) return;
    window.localStorage.setItem('demoRole', key);
    if (onPick) onPick(key);          // update state -> instant view change
    navigate('/');                    // client-side nav to a role-valid route
  };

  return (
    <div
      role="group"
      aria-label="Demo role switcher"
      style={{
        position: 'fixed', bottom: 16, right: 16, zIndex: 10000,
        background: 'white', border: '1px solid #e2e8f0', borderRadius: 999,
        boxShadow: '0 4px 14px rgba(0,0,0,0.18)', padding: 6,
        display: 'flex', alignItems: 'center', gap: 4,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#64748b', padding: '0 8px', letterSpacing: '0.4px' }}>VIEW&nbsp;AS</span>
      {ROLES.map((r) => (
        <button
          key={r.key}
          onClick={() => pick(r.key)}
          aria-pressed={r.key === current}
          style={{
            padding: '6px 14px', borderRadius: 999, border: 'none', cursor: 'pointer',
            fontSize: '0.82rem', fontWeight: 700,
            background: r.key === current ? '#2563eb' : '#f1f5f9',
            color: r.key === current ? 'white' : '#334155',
            transition: 'background 0.15s ease',
          }}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

export default DemoRoleSwitcher;
